/**
 * The big countdown number. Same component renders it in the panel and on
 * stage — only the CSS clamp() changes how large it looks, never the maths
 * (that always comes from src/engine).
 */
import { makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { describeRemaining, formatCountdown } from "./format";
import type { ColourState } from "./types";

const useStyles = makeStyles({
  number: {
    fontVariantNumeric: "tabular-nums",
    fontWeight: tokens.fontWeightBold,
    fontSize: "clamp(2.5rem, 12vw, 7rem)",
    lineHeight: 1,
    margin: 0,
  },
  normal: { color: tokens.colorNeutralForeground1 },
  warning: { color: tokens.colorPaletteMarigoldForeground1 },
  overtime: { color: tokens.colorPaletteRedForeground1 },
  label: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
});

export interface CountdownProps {
  remainingSeconds: number;
  colour: ColourState;
  /** id of the aria-live region announcing hand-offs/time-up, if this
   * countdown should also carry that live-region role (Running.tsx wires
   * this up once per screen, not once per Countdown instance). */
  labelledBy?: string;
}

export function Countdown({ remainingSeconds, colour }: CountdownProps) {
  const styles = useStyles();
  const label = describeRemaining(remainingSeconds);

  return (
    <div>
      <p
        className={mergeClasses(styles.number, styles[colour])}
        aria-hidden="true"
        data-testid="countdown"
      >
        {formatCountdown(remainingSeconds)}
      </p>
      {/* Colour never carries meaning alone: this text label always ships
          alongside the coloured number (BUILD-SPEC.md §7). */}
      <p className={styles.label}>{label}</p>
    </div>
  );
}
